import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useResultsBehindProfile } from './useResultsBehindProfile';
import { useTableSelection } from './useTableSelection';
import { useVariablesPreviewSection } from './useVariablesPreviewSection';
import type {
  AnalysisDefinition,
  AnalysisTable,
  TableGridData,
  TableGridSource,
} from '../api/types';
import { useAnalysisResultsQuery } from '../api/useAnalysisResultsQuery';
import { useAnalysisVersionQuery } from '../api/useAnalysisVersionQuery';
import { selectSourceOrder, selectSources } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type {
  DatasetView,
  SelectedDatasetSource,
  TableSelectorOption,
  UseTablesSectionResult,
} from '../types';
import {
  countDatasetSources,
  countTableRows,
  getDatasetSources,
  getFirstCohortAnalyticalDatasetProfileId,
  getSourcesMissingFromRun,
} from '../utils/analysisSources';
import { getTableLabel } from '../utils/tableLabel';

// Not a real table id — the "Analytical dataset" entry represents the
// definition's `sources` map, not an entry in `tables`.
const ANALYTICAL_DATASET_OPTION_ID = 'analytical-dataset';

const EMPTY_SOURCES: Record<string, TableGridSource> = {};
const EMPTY_TABLES: AnalysisTable[] = [];

export const useTablesSection = (parentLoading = false): UseTablesSectionResult => {
  const { formatMessage } = useIntl();
  const { projectId, analysisProfileId, analysisVersionId } = useAnalysisRouteParams();
  const {
    addLabel: addVariableLabel,
    isPickerOpen: isVariablePickerOpen,
    onAddVariable,
    onClosePicker: onCloseVariablePicker,
    isEditDisabled,
    editLockTooltip,
  } = useVariablesPreviewSection();

  const versionQuery = useAnalysisVersionQuery({ projectId, analysisProfileId, analysisVersionId });
  const definition = versionQuery.data?.data.jsonValue ?? null;
  // The staged (possibly added-to) sources map, once hydrated - adding a
  // variable via the picker edits this, not `definition`.
  const stagedSources = useAnalysisDefinitionStore(selectSources);
  const stagedSourceOrder = useAnalysisDefinitionStore(selectSourceOrder);

  const hasTables = Boolean(definition && definition.tables.length > 0);

  // What the "Analytical dataset" preview counts/lists: pristine `tables`
  // (not editable here) plus the staged `sources` and their order.
  const datasetDefinition = useMemo<AnalysisDefinition | null>(
    () =>
      definition
        ? {
            sources: stagedSources ?? definition.sources,
            sourceOrder: stagedSources ? stagedSourceOrder : definition.sourceOrder,
            tables: definition.tables,
          }
        : null,
    [definition, stagedSources, stagedSourceOrder]
  );

  const options = useMemo<TableSelectorOption[]>(() => {
    if (!definition) return [];
    const tableOptions = definition.tables.map((table, index) => ({
      id: table.tableId,
      label: getTableLabel(table.name, index + 1, formatMessage),
      countLabel: formatMessage(
        { id: 'analysisDetail.tables.rowCount' },
        { count: countTableRows(table) }
      ),
    }));
    return [
      ...tableOptions,
      {
        id: ANALYTICAL_DATASET_OPTION_ID,
        label: formatMessage({ id: 'analysisDetail.tables.analyticalDataset' }),
        countLabel: formatMessage(
          { id: 'analysisDetail.tables.variableCount' },
          { count: countDatasetSources(datasetDefinition) }
        ),
      },
    ];
  }, [definition, datasetDefinition, formatMessage]);

  const {
    selectedId,
    onSelect: setSelection,
    selectedTable: selectedDefinitionTable,
    selectedTableNumber,
    isAnalyticalDatasetSelected,
  } = useTableSelection(definition?.tables ?? EMPTY_TABLES, options);

  const selectedDatasetSources = useMemo<SelectedDatasetSource[]>(
    () =>
      datasetDefinition && isAnalyticalDatasetSelected ? getDatasetSources(datasetDefinition) : [],
    [datasetDefinition, isAnalyticalDatasetSelected]
  );

  // The population's analytical dataset entity -- set once a cohort is
  // linked, well before the analysis has been processed. Showing its grid
  // needs the analysis to actually have run: see `hasAnalyticalDatasetResults`.
  const analyticalDatasetProfileId = useMemo(
    () => getFirstCohortAnalyticalDatasetProfileId(definition),
    [definition]
  );

  // The analysis must have run before the dataset grid has any rows to show.
  // Keyed off the results response (the same source the table shells use), not
  // the profile status: saving an edited definition resets the profile to
  // DRAFT, but the results endpoint keeps returning the last run, so the grid
  // stays alongside the stale banner.
  const resultsQuery = useAnalysisResultsQuery({ projectId, analysisProfileId });
  // Shows the loading skeleton while a finished run's results are still being
  // fetched, instead of the previous run's cells.
  const isResultsBehindProfile = useResultsBehindProfile();
  const hasAnalyticalDatasetResults =
    Boolean(analyticalDatasetProfileId) && Boolean(resultsQuery.data?.data.analysisExecutionId);

  // Which of the Analytical dataset option's two views is showing -- `null`
  // until the viewer explicitly picks one, so it tracks the computed default
  // (Table once results exist, List otherwise) reactively.
  const [selectedDatasetView, setSelectedDatasetView] = useState<DatasetView | null>(null);
  const datasetView: DatasetView =
    selectedDatasetView ?? (hasAnalyticalDatasetResults ? 'table' : 'list');
  const onSelectDatasetView = useCallback((view: DatasetView) => setSelectedDatasetView(view), []);

  // Re-selecting an option (including re-entering the Analytical dataset
  // option) resets back to the default view for its current results state.
  const onSelect = useCallback(
    (id: string) => {
      setSelectedDatasetView(null);
      setSelection(id);
    },
    [setSelection]
  );

  const resultTables = resultsQuery.data?.data.tables ?? null;
  const resultsResolvedSources = resultsQuery.data?.data.resolvedSources;
  const datasetSourcesMissingFromRun = useMemo(
    () => getSourcesMissingFromRun(selectedDatasetSources, resultsResolvedSources),
    [selectedDatasetSources, resultsResolvedSources]
  );
  const selectedResultTable = useMemo(
    () =>
      selectedDefinitionTable && resultTables
        ? resultTables.find((table) => table.tableId === selectedDefinitionTable.tableId) ?? null
        : null,
    [selectedDefinitionTable, resultTables]
  );

  const selectedTable: TableGridData | null = selectedResultTable ?? selectedDefinitionTable;
  const resolvedSources = resultsQuery.data?.data.resolvedSources;
  // A results table is the latest definition's shell filled with the last run's
  // cells, so its rows can name sources that run never resolved: a profile that
  // has never run returns an empty `resolvedSources`, and a variable added after
  // the last run is missing from it. The definition's entries therefore take
  // precedence and the run's fill any remaining gaps.
  const sources = useMemo<Record<string, TableGridSource>>(() => {
    if (!selectedResultTable) return definition?.sources ?? EMPTY_SOURCES;
    return { ...resolvedSources, ...definition?.sources };
  }, [selectedResultTable, resolvedSources, definition]);

  return {
    hasTables,
    emptyTitle: formatMessage({ id: 'analysisDetail.tables.emptyTitle' }),
    emptyBody: formatMessage({ id: 'analysisDetail.tables.emptyBody' }),
    options,
    selectedId,
    onSelect,
    isLoading: parentLoading || versionQuery.isLoading || isResultsBehindProfile,
    selectedTable,
    selectedTableNumber,
    isAnalyticalDatasetSelected,
    selectedDatasetSources,
    analyticalDatasetCount: countDatasetSources(datasetDefinition),
    sources,
    projectId,
    addVariableLabel,
    isVariablePickerOpen,
    onAddVariable,
    onCloseVariablePicker,
    isEditDisabled,
    editLockTooltip,
    analyticalDatasetProfileId,
    hasAnalyticalDatasetResults,
    datasetSourcesMissingFromRun,
    datasetView,
    onSelectDatasetView,
    listViewLabel: formatMessage({ id: 'analysisDetail.tables.preview.listView' }),
    tableViewLabel: formatMessage({ id: 'analysisDetail.tables.preview.tableView' }),
    datasetEmptyHint: formatMessage({ id: 'analysisDetail.tables.preview.datasetEmptyHint' }),
  };
};
